import {
  AutocompleteInput,
  AutocompleteArrayInput,
  Datagrid,
  List,
  ReferenceField,
  ReferenceInput,
  TextField,
  TopToolbar,
  useNotify,
  useRefresh,
  Button,
  Show,
  ReferenceArrayInput,
  Tab,
  TextInput,
  TabbedShowLayout,
  BooleanInput,
} from "react-admin";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@material-ui/core";
import { useForm, useFormState, Form } from "react-final-form";
import WhenFieldChanges from "./whenFieldChanges";
import { gql, useMutation as apolloUseMutation, useQuery } from "@apollo/react-hooks";

const nameLikeSearch = (n) => {
  return {
    username__like: n,
    //   ids: [2]
  };
};

const companySearch = (n) => {
  if (n && n.length > 2) {
    return {
      name__like: n,
    };
  }
};

const sheetFilters = [
  <TextInput label="Title" source="title__like" alwaysOn />,
  <ReferenceInput
    perPage={200}
    label="Project"
    source="project_id"
    reference="Project"
    filterToQuery={companySearch}
    alwaysOn
  >
    <AutocompleteInput label="Comp" source="name" />
  </ReferenceInput>,
];

export const SheetList = (props) => {
  return (
    <List {...props} filter={{ includeShared: true }} filters={sheetFilters} bulkActionButtons={false}  >
      <Datagrid rowClick="show">
        <TextField source="id" />
        <TextField source="title" />
        <ReferenceField label="Project" reference="Project" source="project.id" link={false} sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField label="User" reference="User" source="user.id" link={false} sortable={false}>
          <TextField source="username" />
        </ReferenceField>
      </Datagrid>
    </List>
  );
};

const CopySheet = ({ record }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [companyId, setCompanyId] = useState(-1);
  const [userIds, setUserIds] = useState([]);
  const [projectIds, setProjectIds] = useState([]);
  const [overrideExisting, setOverrideExisting] = useState(false);

  const { data: usersData } = useQuery(
    gql`
      query GetUsers($companyId: Int!) {
        allUsers(filter: { company_id: $companyId }, perPage: 1000) {
          id
          username
        }
      }
    `,
    {
      skip: companyId <= 0,
      variables: { companyId }
    }
  );

  const [copySheet] = apolloUseMutation(gql`
    mutation copySheetForUsers($id: Int!, $userIds: [Int!]!, $projectIds: [Int!], $overrideExisting: Boolean) {
      copySheetForUsers(id: $id, userIds: $userIds, projectIds: $projectIds, overrideExisting: $overrideExisting) {
        id
        title
        __typename
      }
    }
  `);

  const handleClick = () => setOpen(true);
  const handleDialogClose = () => {
    setOpen(false);
  };

  const handleEnable = async (formValues) => {
    try {
      await copySheet({
        variables: {
          id: record.id,
          userIds,
          projectIds: projectIds.length > 0 ? projectIds : null,
          overrideExisting: formValues?.overrideExisting || false
        }
      });
      notify("Saved");
      refresh();
      setOpen(false);
    } catch (e) {
      notify(e.toString());
    }
  };
  return (
    <>
      <Button label="Copy Sheet" onClick={handleClick} />
      <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="md">
        <DialogTitle>Copy Sheet</DialogTitle>
        <DialogContent>
          <Form
            onSubmit={handleEnable}
            initialValues={{ usersIds: userIds, projectIds: projectIds, overrideExisting: false }}
            render={({ handleSubmit, form, values }) => {
              const handleSelectAll = () => {
                if (usersData && usersData.allUsers) {
                  const allUserIds = usersData.allUsers.map(user => user.id);
                  form.change('usersIds', allUserIds);
                  setUserIds(allUserIds);
                }
              };

              const handleDeselectAll = () => {
                form.change('usersIds', []);
                setUserIds([]);
              };

              return (
                <>
                  <ReferenceInput
                    perPage={200}
                    source="companiesIds"
                    reference="Company"
                    resource="Company"
                    filterToQuery={companySearch}
                    onChange={setCompanyId}
                  >
                    <AutocompleteInput optionText="name" />
                  </ReferenceInput>
                  {companyId > 0 && (
                    <>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <ReferenceArrayInput
                            perPage={1000}
                            label="User"
                            source="usersIds"
                            reference="User"
                            filterToQuery={nameLikeSearch}
                            filter={{ company_id: companyId }}
                            onChange={setUserIds}
                          >
                            <AutocompleteArrayInput optionText="username" />
                          </ReferenceArrayInput>
                        </div>
                        <Button
                          label="Select All"
                          onClick={handleSelectAll}
                          style={{ marginBottom: '8px' }}
                        />
                        <Button
                          label="Deselect All"
                          onClick={handleDeselectAll}
                          style={{ marginBottom: '8px' }}
                        />
                      </div>
                      <ReferenceArrayInput
                        perPage={200}
                        label="Projects (Optional)"
                        source="projectIds"
                        reference="Project"
                        filterToQuery={companySearch}
                        filter={{ company_id: companyId }}
                        onChange={setProjectIds}
                      >
                        <AutocompleteArrayInput optionText="name" />
                      </ReferenceArrayInput>
                    </>
                  )}
                  <BooleanInput
                    source="overrideExisting"
                    label="Override Existing Sheets"
                  />
                  <Button label="Copy" onClick={handleSubmit} />
                </>
              );
            }}
          ></Form>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SheetShowActions = ({ basePath, data, record }) => (
  <TopToolbar>
    <CopySheet basePath={basePath} record={data} />
  </TopToolbar>
);

export const SheetShow = (props) => {
  return (
    <Show title={props.title} {...props} actions={<SheetShowActions />}>
      <TabbedShowLayout syncWithLocation={false}>
        <Tab label="Summary">
          <TextField source="id" />
          <TextField source="title" />
          <ReferenceField label="User" reference="User" source="user.id" link={false} sortable={false}>
            <TextField source="username" />
          </ReferenceField>
          <ReferenceField label="Project" reference="Project" source="project.id" link={false} sortable={false}>
            <TextField source="name" />
          </ReferenceField>
        </Tab>
      </TabbedShowLayout>
    </Show>
  );
};
